import { useEffect, useMemo, useState } from "react";
import {
	Box,
	CircularProgress,
	IconButton,
	MenuItem,
	Paper,
	Select,
	Switch,
	Typography,
} from "@mui/material";

import {
	fetchData,
	FetchDataFailureAction,
	FetchDataSuccessAction,
} from "../../../store/actions";
import { getVelocityData } from "../../../Service/Apis";
import { ThunkDispatch } from "@reduxjs/toolkit";
import { RootState } from "../../../store/Store";
import { useDispatch, useSelector } from "react-redux";
import MonthButtonGroup from "../../utilization/MonthsButtonGroup";
import DownloadForOfflineIcon from "@mui/icons-material/DownloadForOffline";
import { saveAs } from "file-saver";
import { useCustomToast } from "../../Methods/SnackBar";
import LineChartComponent from "../../charts/LineChart";
import { set } from "date-fns";

const VelocityChart = () => {
	const dispatch2: ThunkDispatch<
		RootState,
		null,
		FetchDataSuccessAction | FetchDataFailureAction
	> = useDispatch();
	const [velocityData, setVelocityData] = useState<any>([]);
	const [teamName, setTeamName] = useState<string>("");
	const [month, setMonth] = useState<string>("1");
	const [selectedFormat, setSelectedFormat] = useState<string>("excel");
	const showToast = useCustomToast();
	const [selectedEmployees, setSelectedEmployees] = useState<string[]>([]);
	const [loading, setLoading] = useState(false);
	const [downloading, setDownloading] = useState(false);
	const [selectAll, setSelectAll] = useState(true);

	const nrole = useSelector((state: any) => state.auth.selectedRole);
	const paramsData = {
		role: nrole,
	};

	const fetchVelocityData = async (selectedMonth: string) => {
		try {
			setLoading(true);
			const res = await dispatch2(
				fetchData(`${getVelocityData}${selectedMonth}`, paramsData)
			);
			setMonth(selectedMonth);
			setVelocityData(res);

			// Set all employee IDs as selected by default
			const allEmployeeNames = res?.teams
				? res?.teams?.flatMap((team: any) =>
						team.issues.flatMap((issue: any) =>
							issue.velocity.map((item: any) => item.assignee_name)
						)
				  ) || []
				: res?.data?.flatMap((entry: any) =>
						entry.velocity.map((item: any) => item.assignee_name)
				  ) || [];

			setSelectedEmployees(allEmployeeNames);
		} catch (error) {
			console.error(error);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		fetchVelocityData("1");
	}, []);

	const handleMonthChange = (selectedMonth: string) => {
		fetchVelocityData(selectedMonth);
	};

	const buttonData = [
		{ label: "1M", onClick: () => handleMonthChange("1") },
		{ label: "3M", onClick: () => handleMonthChange("3") },
		{ label: "6M", onClick: () => handleMonthChange("6") },
		{ label: "1Y", onClick: () => handleMonthChange("12") },
	];

	const computedData = useMemo(() => {
		if (!velocityData) return [];

		// Check if team data exists and find the selected team
		const selectedTeam =
			velocityData?.teams?.find((team: any) => team.team_name === teamName) ||
			velocityData?.teams?.[0];

		setTeamName(selectedTeam?.team_name);

		if (selectedTeam) {
			// Map the issues for the selected team
			return selectedTeam.issues.flatMap((issue: any) =>
				issue.velocity.map((entry: any) => ({
					employee_name: entry.assignee_name, // Assignee name
					week: issue.week_end, // Week range
					percent: entry.hours_per_point, // Hours per point
				}))
			);
		}

		// If no team is selected, process the standalone `data` array
		return (
			velocityData?.data?.flatMap((issue: any) =>
				issue.velocity.map((entry: any) => ({
					employee_name: entry.assignee_name,
					week: issue.week_end,
					percent: entry.hours_per_point,
				}))
			) || []
		);
	}, [velocityData, teamName]);

	const onDownload = async () => {
		try {
			setDownloading(true);
			if (nrole.toLowerCase() !== "employee") {
				if (!teamName) {
					throw new Error("Please select a team");
				}

				// Find the selected team object to get its ID
				const selectedTeam = velocityData.teams.find(
					(team: any) => team.team_name === teamName
				);

				if (!selectedTeam) {
					throw new Error("Selected team not found");
				}

				const downloadParams = {
					...paramsData,
					download: selectedFormat,
					team_id: selectedTeam.team_id, // Add the team ID parameter
				};

				const res = await dispatch2(
					fetchData(`${getVelocityData}${month}`, downloadParams, {
						responseType: "blob",
					})
				);

				// Include team name in filename
				saveAs(
					res,
					`velocity-${teamName.replace(/\s+/g, "-")}.${
						selectedFormat === "excel" ? "xlsx" : selectedFormat
					}`
				);
			} else {
				// For employee role, no team selection is needed
				const downloadParams = {
					...paramsData,
					download: selectedFormat,
				};

				const res = await dispatch2(
					fetchData(`${getVelocityData}${month}`, downloadParams, {
						responseType: "blob",
					})
				);

				saveAs(
					res,
					`velocity.${selectedFormat === "excel" ? "xlsx" : selectedFormat}`
				);
			}
		} catch (error: any) {
			console.error("Error downloading file:", error);
			showToast(error, "error", true);
		} finally {
			setDownloading(false);
		}
	};

	const handleEmployeeSelection = (employeeName: string) => {
		setSelectedEmployees((prev) => {
			const newSelection = prev.includes(employeeName)
				? prev.filter((name) => name !== employeeName)
				: [...prev, employeeName];

			// Update selectAll state based on whether all employees are selected
			const uniqueEmployees = Array.from(
				new Set(computedData.map((item: any) => item.employee_name))
			);

			if (newSelection.length === uniqueEmployees.length) {
				setSelectAll(true);
			}
			if (newSelection.length === 0) {
				setSelectAll(false);
			}

			return newSelection;
		});
	};

	// Add helper function to get all employee names
	const getAllEmployeeNames = () => {
		return velocityData?.teams
			? velocityData?.teams?.flatMap((team: any) =>
					team.issues.flatMap((issue: any) =>
						issue.velocity.map((item: any) => item.assignee_name)
					)
			  ) || []
			: velocityData?.data?.flatMap((entry: any) =>
					entry.velocity.map((item: any) => item.assignee_name)
			  ) || [];
	};

	const handleSwitchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const checked = event.target.checked;
		setSelectAll(checked);

		if (checked) {
			// Select all employees
			setSelectedEmployees(getAllEmployeeNames());
		} else {
			// Deselect all employees
			setSelectedEmployees([]);
		}
	};

	return (
		<>
			<Paper sx={{ m: 2, p: 4 }}>
				<Box display="flex" justifyContent="space-between" alignItems="center">
					<Box
						display="flex"
						justifyContent="center"
						alignItems="center"
						flexDirection="column"
						gap={2}
					>
						<Typography variant="h4">Velocity Chart</Typography>
						<MonthButtonGroup buttonData={buttonData} />
					</Box>
					<Box
						display="flex"
						justifyContent="center"
						alignItems="center"
						flexDirection="column"
						gap={2}
					>
						{velocityData?.teams && (
							<Box
								display="flex"
								justifyContent="center"
								alignItems="center"
								gap={2}
							>
								<Typography
									variant="h6"
									width={200}
									sx={{
										fontSize: "1rem",
										display: "inline",
									}}
								>
									Select Team
								</Typography>
								<Select
									labelId="team-select-label"
									id="team-select"
									value={teamName}
									label="Team"
									size="small"
									onChange={(e) => setTeamName(e.target.value)}
									sx={{
										width: "100%",
									}}
								>
									{velocityData?.teams?.map((team: any) => (
										<MenuItem key={team.team_id} value={team.team_name}>
											{team.team_name}
										</MenuItem>
									))}
								</Select>
							</Box>
						)}
						<Box
							display="flex"
							justifyContent="center"
							gap={2}
							alignItems="center"
						>
							{downloading ? (
								<Box
									display="flex"
									justifyContent="center"
									alignItems="center"
									gap={2}
								>
									<CircularProgress size="1rem" />
									<Typography>Downloading...</Typography>
								</Box>
							) : (
								<Box
									display="flex"
									justifyContent="center"
									alignItems="center"
									gap={2}
								>
									<Box
										display="flex"
										justifyContent="center"
										alignItems="center"
										gap={2}
									>
										<Typography
											variant="h6"
											width={200}
											sx={{
												fontSize: "1rem",
												display: "inline",
											}}
										>
											Select format
										</Typography>
										<Select
											labelId="format-select-label"
											id="forma-select"
											value={selectedFormat}
											label="format"
											size="small"
											onChange={(e) => setSelectedFormat(e.target.value)}
											sx={{
												width: "100%",
											}}
										>
											<MenuItem key="excel" value="excel">
												excel
											</MenuItem>
											<MenuItem key="csv" value="csv">
												csv
											</MenuItem>
											<MenuItem key="json" value="json">
												json
											</MenuItem>
										</Select>
									</Box>
									<IconButton
										onClick={() => {
											onDownload();
										}}
									>
										<DownloadForOfflineIcon />
									</IconButton>
								</Box>
							)}
						</Box>
					</Box>
				</Box>
				<Box
					display="flex"
					justifyContent="flex-end"
					alignItems="center"
					gap={2}
					m={2}
					width="100%"
				>
					<Typography>Select All Employees</Typography>
					<Switch
						checked={selectAll}
						onChange={handleSwitchChange}
						color="primary"
					/>
				</Box>
				{loading ? (
					<Box
						display="flex"
						justifyContent="center"
						alignItems="center"
						width="100%"
						height={200}
					>
						<CircularProgress />
					</Box>
				) : (
					<LineChartComponent
						data={computedData || []}
						YAxisLabel="Time Spent (hours)"
						selectedEmployees={selectedEmployees}
						onEmployeeSelection={handleEmployeeSelection}
					/>
				)}
			</Paper>
		</>
	);
};

export default VelocityChart;
